import React, { useEffect, useState } from 'react';
import MonitoringDisclosureCard from '../../components/workforceMonitoring/MonitoringDisclosureCard';
import BreakGlassMasterPasswordCard from '../../components/workforceMonitoring/BreakGlassMasterPasswordCard';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  MenuItem,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const bool = (value: boolean) => String(value);

const parseRecipientsText = (text: string): string[] =>
  text.split(/[,;\n]+/).map((part) => part.trim()).filter(Boolean);

const recipientsToText = (value: string[] | undefined): string =>
  Array.isArray(value) ? value.join(', ') : '';

const ConfigurationPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canConfigureLiveScreen = permissions.includes('monitoring.live_screen.configure');
  const canReadRemoteControl = permissions.includes('monitoring.remote_control.read')
    || permissions.includes('monitoring.remote_control.start');
  const canEditRemoteControl = permissions.includes('monitoring.remote_control.start');
  const canViewDailyEmailConfig = permissions.includes('monitoring.activity_reports.configure')
    || permissions.includes('monitoring.console.read');
  const canConfigureDailyEmail = permissions.includes('monitoring.activity_reports.configure');
  const canConfigureEmergency = permissions.includes('monitoring.emergency_access.configure');
  const canReadEmergency = permissions.includes('monitoring.emergency_access.read')
    || canConfigureEmergency;
  const canConfigureProductivity = permissions.includes('monitoring.productivity.configure');
  const canReadProductivity = permissions.includes('monitoring.productivity.read')
    || canConfigureProductivity;
  const canManageClassifications = permissions.includes('monitoring.productivity.classifications.manage');
  const canReadClassifications = permissions.includes('monitoring.productivity.classifications.read')
    || canManageClassifications;
  const canReadEnforcementPolicies = permissions.includes('monitoring.enforcement_policies.read')
    || permissions.includes('monitoring.console.read');

  const { data, isLoading } = useQuery({
    queryKey: ['monitoringConfiguration'],
    queryFn: () => apiClient.monitoring.getConfiguration(),
  });

  const { data: liveScreenData, isLoading: liveScreenLoading } = useQuery({
    queryKey: ['monitoringLiveScreenConfig'],
    queryFn: () => apiClient.monitoring.getLiveScreenConfig(),
    enabled: canConfigureLiveScreen,
  });

  const { data: remoteControlData, isLoading: remoteControlLoading } = useQuery({
    queryKey: ['monitoringRemoteControlConfig'],
    queryFn: () => apiClient.monitoring.getRemoteControlConfig(),
    enabled: canReadRemoteControl,
  });

  const [form, setForm] = useState<any>(null);
  const [liveForm, setLiveForm] = useState<any>(null);
  const [remoteForm, setRemoteForm] = useState<any>(null);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [liveSaved, setLiveSaved] = useState(false);
  const [remoteSaved, setRemoteSaved] = useState(false);
  const [prodTab, setProdTab] = useState(0);
  const [classForm, setClassForm] = useState({
    target_type: 'APPLICATION',
    target_key: '',
    classification: 'PRODUCTIVE',
    campaign_id: '',
  });

  const { data: classificationsData, refetch: refetchClassifications } = useQuery({
    queryKey: ['productivityClassifications'],
    queryFn: () => apiClient.monitoring.listProductivityClassifications({ include_disabled: 'true' }),
    enabled: canReadClassifications,
  });

  const { data: unclassifiedData, refetch: refetchUnclassified } = useQuery({
    queryKey: ['productivityUnclassified'],
    queryFn: () => apiClient.monitoring.getUnclassifiedProductivityCandidates({}),
    enabled: canReadClassifications,
  });

  const saveClassificationMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => apiClient.monitoring.upsertProductivityClassification(payload),
    onSuccess: () => {
      refetchClassifications();
      refetchUnclassified();
      setClassForm((prev) => ({ ...prev, target_key: '' }));
    },
  });

  const deleteClassificationMutation = useMutation({
    mutationFn: (id: string) => apiClient.monitoring.deleteProductivityClassification(id),
    onSuccess: () => {
      refetchClassifications();
      refetchUnclassified();
    },
  });

  useEffect(() => {
    if (data?.data?.data) {
      setForm(data.data.data);
    }
  }, [data]);

  useEffect(() => {
    if (liveScreenData?.data?.data) {
      setLiveForm(liveScreenData.data.data);
    }
  }, [liveScreenData]);

  useEffect(() => {
    if (remoteControlData?.data?.data) {
      const cfg = remoteControlData.data.data;
      setRemoteForm({
        ...cfg,
        sessionRetentionDays: cfg.sessionRetentionDays
          ?? cfg.remote_control_session_retention_days
          ?? 90,
        chatRetentionDays: cfg.chatRetentionDays
          ?? cfg.remote_control_chat_retention_days
          ?? 90,
        transferRetentionDays: cfg.transferRetentionDays
          ?? cfg.remote_control_transfer_retention_days
          ?? 30,
      });
    }
  }, [remoteControlData]);

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, string | number | boolean>) => apiClient.monitoring.updateConfiguration(payload),
    onSuccess: () => {
      setSaveError(null);
      queryClient.invalidateQueries({ queryKey: ['monitoringConfiguration'] });
      setSaved(true);
    },
    onError: (err: any) => {
      setSaved(false);
      setSaveError(err?.response?.data?.message || 'Failed to save configuration (the change did not persist).');
    },
  });

  const saveLiveMutation = useMutation({
    mutationFn: (payload: Record<string, any>) => apiClient.monitoring.updateLiveScreenConfig(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitoringLiveScreenConfig'] });
      setLiveSaved(true);
    },
  });

  const saveRemoteMutation = useMutation({
    mutationFn: (payload: Record<string, any>) => apiClient.monitoring.updateRemoteControlConfig(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitoringRemoteControlConfig'] });
      setRemoteSaved(true);
    },
  });

  if (isLoading || !form) return <CircularProgress size={24} />;

  const updateFlat = (key: string, value: string | number | boolean) => {
    setSaved(false);
    setForm((prev: any) => {
      const next = { ...prev };
      if (key.startsWith('agent.')) next.agent = { ...next.agent, [key.replace('agent.', '')]: value };
      if (key.startsWith('work_session.')) next.work_session = { ...next.work_session, [key.replace('work_session.', '')]: value };
      if (key.startsWith('application_monitoring.')) next.application_monitoring = { ...next.application_monitoring, [key.replace('application_monitoring.', '')]: value };
      if (key.startsWith('web_monitoring.')) next.web_monitoring = { ...next.web_monitoring, [key.replace('web_monitoring.', '')]: value };
      if (key.startsWith('action_recording.')) next.action_recording = { ...next.action_recording, [key.replace('action_recording.', '')]: value };
      if (key.startsWith('alerts.')) next.alerts = { ...next.alerts, [key.replace('alerts.', '')]: value };
      if (key.startsWith('emergency_access.')) next.emergency_access = { ...next.emergency_access, [key.replace('emergency_access.', '')]: value };
      if (key.startsWith('daily_workforce_report.')) {
        next.daily_workforce_report = { ...(next.daily_workforce_report || {}), [key.replace('daily_workforce_report.', '')]: value };
      }
      if (key.startsWith('productivity.')) {
        const rest = key.replace('productivity.', '');
        const prod = { ...(next.productivity || {}) };
        if (rest.startsWith('weights.')) {
          prod.weights = { ...(prod.weights || {}), [rest.replace('weights.', '')]: value };
        } else if (rest.startsWith('thresholds.')) {
          prod.thresholds = { ...(prod.thresholds || {}), [rest.replace('thresholds.', '')]: value };
        } else {
          prod[rest] = value;
        }
        next.productivity = prod;
      }
      return next;
    });
  };

  const handleSave = () => {
    const payload: Record<string, string | number | boolean> = {
      expected_agent_version: form.agent.expected_agent_version,
      heartbeat_threshold_seconds: form.agent.heartbeat_threshold_seconds,
      offline_threshold_seconds: form.agent.offline_threshold_seconds,
      early_clock_in_minutes: form.work_session.early_clock_in_minutes,
      idle_lock_threshold_seconds: form.work_session.idle_lock_threshold_seconds,
      bathroom_alert_threshold_seconds: form.work_session.bathroom_alert_threshold_seconds ?? 600,
      require_teks_auth_after_unlock: bool(form.work_session.require_teks_auth_after_unlock),
      require_teks_auth_after_lunch: bool(form.work_session.require_teks_auth_after_lunch),
      enable_application_tracking: bool(form.application_monitoring.enable_tracking),
      enable_application_name: bool(form.application_monitoring.capture_application_name),
      enable_process_name: bool(form.application_monitoring.capture_process_name),
      enable_window_title: bool(form.application_monitoring.capture_window_title),
      activity_retention_days: form.application_monitoring.retention_days,
      enable_browser_domain_tracking: bool(form.web_monitoring.enable),
      enable_page_title_tracking: bool(form.web_monitoring.page_title),
      enable_full_url_tracking: bool(form.web_monitoring.full_url),
      enable_query_string_tracking: bool(form.web_monitoring.query_string),
      enable_incognito_tracking: bool(form.web_monitoring.incognito),
      web_activity_retention_days: form.web_monitoring.retention_days,
      action_recording_enabled: bool(form.action_recording?.enabled ?? true),
      action_uia_capture_enabled: bool(form.action_recording?.ui_automation ?? true),
      action_browser_capture_enabled: bool(form.action_recording?.browser_actions ?? true),
      action_evidence_snapshots_enabled: bool(form.action_recording?.evidence_snapshots ?? true),
      action_evidence_event_types: form.action_recording?.evidence_event_types ?? '',
      action_evidence_retention_days: form.action_recording?.evidence_retention_days ?? 14,
      action_metadata_retention_days: form.action_recording?.metadata_retention_days ?? 90,
      action_capture_before_clock_in: bool(form.action_recording?.capture_before_clock_in ?? true),
      action_capture_during_break: bool(form.action_recording?.capture_during_break ?? false),
      action_capture_during_lunch: bool(form.action_recording?.capture_during_lunch ?? false),
      action_capture_after_clock_out: bool(form.action_recording?.capture_after_clock_out ?? false),
      action_capture_during_emergency: bool(form.action_recording?.capture_during_emergency ?? false),
      action_snapshot_max_width: form.action_recording?.snapshot_max_width ?? 1280,
      action_snapshot_quality: form.action_recording?.snapshot_quality ?? 70,
      alert_offline_during_shift: bool(form.alerts.offline_during_shift),
      alert_idle_too_long: bool(form.alerts.idle_too_long),
      alert_locked_too_long: bool(form.alerts.locked_too_long),
      alert_bathroom_too_long: bool(form.alerts.bathroom_too_long ?? true),
      alert_campaign_mismatch: bool(form.alerts.campaign_mismatch),
      alert_old_agent: bool(form.alerts.old_agent),
      alert_browser_missing: bool(form.alerts.browser_missing),
    };

    if (canConfigureEmergency && form.emergency_access) {
      Object.assign(payload, {
        alert_emergency_bypass: bool(form.alerts.emergency_bypass),
        emergency_bypass_enabled: bool(form.emergency_access.enabled),
        emergency_bypass_expiration_minutes: form.emergency_access.code_expiration_minutes,
        emergency_bypass_reason_required: bool(form.emergency_access.reason_required),
      });
    }

    if (canConfigureDailyEmail && form.daily_workforce_report) {
      const dwr = form.daily_workforce_report;
      Object.assign(payload, {
        daily_workforce_report_email_enabled: bool(Boolean(dwr.enabled)),
        daily_workforce_report_delivery_time: dwr.delivery_time || '19:00',
        daily_workforce_report_timezone: dwr.timezone || 'America/Chicago',
        daily_workforce_report_recipients: JSON.stringify(
          typeof dwr.recipients === 'string'
            ? parseRecipientsText(dwr.recipients)
            : (Array.isArray(dwr.recipients) ? dwr.recipients : []),
        ),
        daily_workforce_report_cc_recipients: JSON.stringify(
          typeof dwr.cc_recipients === 'string'
            ? parseRecipientsText(dwr.cc_recipients)
            : (Array.isArray(dwr.cc_recipients) ? dwr.cc_recipients : []),
        ),
        daily_workforce_report_include_by_person: bool(Boolean(dwr.include_by_person)),
        daily_workforce_report_include_by_computer: bool(Boolean(dwr.include_by_computer)),
        daily_workforce_report_include_apps: bool(Boolean(dwr.include_apps)),
        daily_workforce_report_include_domains: bool(Boolean(dwr.include_domains)),
        daily_workforce_report_include_evidence: bool(Boolean(dwr.include_evidence)),
        daily_workforce_report_include_attention: bool(Boolean(dwr.include_attention)),
        daily_workforce_report_include_narrative: bool(Boolean(dwr.include_narrative)),
        daily_workforce_report_include_unlinked: bool(Boolean(dwr.include_unlinked)),
        daily_workforce_report_include_missing: bool(Boolean(dwr.include_missing)),
      });
    }

    if (canConfigureProductivity && form.productivity) {
      const p = form.productivity;
      Object.assign(payload, {
        productivity_metrics_enabled: bool(Boolean(p.metrics_enabled ?? true)),
        productivity_score_enabled: bool(Boolean(p.score_enabled ?? false)),
        productivity_score_weights: JSON.stringify(p.weights || {
          attendance: 30, active_work: 30, productive_apps: 25, schedule_compliance: 15,
        }),
        productivity_score_thresholds: JSON.stringify(p.thresholds || {}),
        productivity_score_bands: JSON.stringify(p.score_bands || []),
      });
    }

    saveMutation.mutate(payload);
  };

  const handleSaveLive = () => {
    if (!liveForm) return;
    setLiveSaved(false);
    saveLiveMutation.mutate({
      enabled: liveForm.enabled,
      // Attendance capture toggles removed from UI — always allow (device-centric Live Screen).
      captureBeforeClockIn: true,
      captureWhileWorking: true,
      captureDuringBreak: true,
      captureDuringLunch: true,
      captureAfterClockOut: true,
      captureDuringEmergencyBypass: true,
      captureWhenIdle: true,
      thumbnail: liveForm.thumbnail,
      detail: liveForm.detail,
      maxActiveThumbnails: liveForm.maxActiveThumbnails,
      staleFrameThresholdSeconds: liveForm.staleFrameThresholdSeconds,
    });
  };

  const patchLive = (patch: Record<string, any>) => {
    setLiveSaved(false);
    setLiveForm((prev: any) => ({ ...prev, ...patch }));
  };

  const patchRemote = (patch: Record<string, any>) => {
    setRemoteSaved(false);
    setRemoteForm((prev: any) => ({ ...prev, ...patch }));
  };

  const handleSaveRemote = () => {
    if (!remoteForm || !canEditRemoteControl) return;
    setRemoteSaved(false);
    saveRemoteMutation.mutate({
      enabled: Boolean(remoteForm.enabled),
      inactivityTimeoutMinutes: Number(remoteForm.inactivityTimeoutMinutes) || 15,
      showIndicatorToEmployee: Boolean(remoteForm.showIndicatorToEmployee),
      clipboardMaxBytes: Number(remoteForm.clipboardMaxBytes) || 1048576,
      chatEnabled: remoteForm.chatEnabled !== false,
      chatMaxMessageChars: Number(remoteForm.chatMaxMessageChars) || 2000,
      fileMaxBytes: Number(remoteForm.fileMaxBytes) || 52428800,
      supportFolderName: String(remoteForm.supportFolderName || 'TEKS Support').trim() || 'TEKS Support',
      filesEnabled: remoteForm.filesEnabled !== false,
      actionsEnabled: remoteForm.actionsEnabled !== false,
      sessionRetentionDays: Number(remoteForm.sessionRetentionDays) || 90,
      chatRetentionDays: Number(remoteForm.chatRetentionDays) || 90,
      transferRetentionDays: Number(remoteForm.transferRetentionDays) || 30,
      // Also send setting keys for backends that accept REMOTE_CONTROL_SETTING_KEYS directly
      remote_control_file_max_bytes: Number(remoteForm.fileMaxBytes) || 52428800,
      remote_control_file_support_folder_name:
        String(remoteForm.supportFolderName || 'TEKS Support').trim() || 'TEKS Support',
      remote_control_files_enabled: remoteForm.filesEnabled !== false,
      remote_control_actions_enabled: remoteForm.actionsEnabled !== false,
      remote_control_session_retention_days: Number(remoteForm.sessionRetentionDays) || 90,
      remote_control_chat_retention_days: Number(remoteForm.chatRetentionDays) || 90,
      remote_control_transfer_retention_days: Number(remoteForm.transferRetentionDays) || 30,
    });
  };

  const patchProfile = (profile: 'thumbnail' | 'detail', key: string, value: number) => {
    setLiveSaved(false);
    setLiveForm((prev: any) => ({
      ...prev,
      [profile]: { ...prev[profile], [key]: value },
    }));
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        TEKS TIME collects application names, process names, window titles, browser domains, and page titles when enabled.
        Keystrokes, clipboard, passwords, and form contents are never collected.
      </Typography>

      {canReadEnforcementPolicies && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">Enforcement Policies</Typography>
              <Typography variant="body2" color="text.secondary">
                Configure Windows agent enforcement intensity by role (reminders, warnings, blockers).
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => navigate('/workforce-monitoring/configuration/enforcement-policies')}
            >
              Open Enforcement Policies
            </Button>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>Agent</Typography>
            <TextField fullWidth margin="dense" label="Expected Agent Version" value={form.agent.expected_agent_version} onChange={(e) => updateFlat('agent.expected_agent_version', e.target.value)} />
            <TextField fullWidth margin="dense" type="number" label="Heartbeat Threshold (sec)" value={form.agent.heartbeat_threshold_seconds} onChange={(e) => updateFlat('agent.heartbeat_threshold_seconds', Number(e.target.value))} />
            <TextField fullWidth margin="dense" type="number" label="Offline Threshold (sec)" value={form.agent.offline_threshold_seconds} onChange={(e) => updateFlat('agent.offline_threshold_seconds', Number(e.target.value))} />
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>Work Session</Typography>
            <TextField fullWidth margin="dense" type="number" label="Early Clock-In Minutes" value={form.work_session.early_clock_in_minutes} onChange={(e) => updateFlat('work_session.early_clock_in_minutes', Number(e.target.value))} />
            <TextField fullWidth margin="dense" type="number" label="Idle Threshold (sec)" value={form.work_session.idle_lock_threshold_seconds} onChange={(e) => updateFlat('work_session.idle_lock_threshold_seconds', Number(e.target.value))} />
            <TextField fullWidth margin="dense" type="number" label="Bathroom Alert Threshold (sec)" value={form.work_session.bathroom_alert_threshold_seconds ?? 600} onChange={(e) => updateFlat('work_session.bathroom_alert_threshold_seconds', Number(e.target.value))} helperText="Default 600 (10 minutes). Used by Live Status and BATHROOM_TOO_LONG alerts." />
            <FormControlLabel control={<Switch checked={form.work_session.require_teks_auth_after_unlock} onChange={(e) => updateFlat('work_session.require_teks_auth_after_unlock', e.target.checked)} />} label="Require TEKS auth after unlock" />
            <FormControlLabel control={<Switch checked={form.work_session.require_teks_auth_after_lunch} onChange={(e) => updateFlat('work_session.require_teks_auth_after_lunch', e.target.checked)} />} label="Require TEKS auth after lunch" />
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>Application Monitoring</Typography>
            <FormControlLabel control={<Switch checked={form.application_monitoring.enable_tracking} onChange={(e) => updateFlat('application_monitoring.enable_tracking', e.target.checked)} />} label="Enable Application Tracking" />
            <FormControlLabel control={<Switch checked={form.application_monitoring.capture_application_name} onChange={(e) => updateFlat('application_monitoring.capture_application_name', e.target.checked)} />} label="Capture Application Name" />
            <FormControlLabel control={<Switch checked={form.application_monitoring.capture_process_name} onChange={(e) => updateFlat('application_monitoring.capture_process_name', e.target.checked)} />} label="Capture Process Name" />
            <FormControlLabel control={<Switch checked={form.application_monitoring.capture_window_title} onChange={(e) => updateFlat('application_monitoring.capture_window_title', e.target.checked)} />} label="Capture Window Title" />
            <TextField fullWidth margin="dense" type="number" label="Retention (days)" value={form.application_monitoring.retention_days} onChange={(e) => updateFlat('application_monitoring.retention_days', Number(e.target.value))} />
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>Web Monitoring</Typography>
            <FormControlLabel control={<Switch checked={form.web_monitoring.enable} onChange={(e) => updateFlat('web_monitoring.enable', e.target.checked)} />} label="Enable Browser Monitoring" />
            <FormControlLabel control={<Switch checked={form.web_monitoring.page_title} onChange={(e) => updateFlat('web_monitoring.page_title', e.target.checked)} />} label="Page Title" />
            <FormControlLabel control={<Switch checked={form.web_monitoring.full_url} onChange={(e) => updateFlat('web_monitoring.full_url', e.target.checked)} />} label="Full URL (off by default)" />
            <FormControlLabel control={<Switch checked={form.web_monitoring.query_string} onChange={(e) => updateFlat('web_monitoring.query_string', e.target.checked)} />} label="Query String" />
            <FormControlLabel control={<Switch checked={form.web_monitoring.incognito} onChange={(e) => updateFlat('web_monitoring.incognito', e.target.checked)} />} label="Incognito" />
            <TextField fullWidth margin="dense" type="number" label="Retention (days)" value={form.web_monitoring.retention_days} onChange={(e) => updateFlat('web_monitoring.retention_days', Number(e.target.value))} />
          </CardContent></Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined"><CardContent>
            <Typography variant="h6" gutterBottom>Action Recording</Typography>
            <FormControlLabel control={<Switch checked={Boolean(form.action_recording?.enabled)} onChange={(e) => updateFlat('action_recording.enabled', e.target.checked)} />} label="Enable action recording" />
            <FormControlLabel control={<Switch checked={Boolean(form.action_recording?.ui_automation)} onChange={(e) => updateFlat('action_recording.ui_automation', e.target.checked)} />} label="UI automation actions" />
            <FormControlLabel control={<Switch checked={Boolean(form.action_recording?.browser_actions)} onChange={(e) => updateFlat('action_recording.browser_actions', e.target.checked)} />} label="Browser actions" />
            <FormControlLabel control={<Switch checked={Boolean(form.action_recording?.evidence_snapshots)} onChange={(e) => updateFlat('action_recording.evidence_snapshots', e.target.checked)} />} label="Evidence snapshots" />
            <TextField fullWidth margin="dense" label="Evidence event types" value={form.action_recording?.evidence_event_types || ''} onChange={(e) => updateFlat('action_recording.evidence_event_types', e.target.value)} helperText="Comma-separated event types eligible for snapshots" />
            <TextField fullWidth margin="dense" type="number" label="Evidence retention (days)" value={form.action_recording?.evidence_retention_days ?? 14} onChange={(e) => updateFlat('action_recording.evidence_retention_days', Number(e.target.value))} />
            <TextField fullWidth margin="dense" type="number" label="Metadata retention (days)" value={form.action_recording?.metadata_retention_days ?? 90} onChange={(e) => updateFlat('action_recording.metadata_retention_days', Number(e.target.value))} />
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Capture when</Typography>
            <FormControlLabel control={<Switch checked={Boolean(form.action_recording?.capture_before_clock_in)} onChange={(e) => updateFlat('action_recording.capture_before_clock_in', e.target.checked)} />} label="Before clock-in" />
            <FormControlLabel control={<Switch checked={Boolean(form.action_recording?.capture_during_break)} onChange={(e) => updateFlat('action_recording.capture_during_break', e.target.checked)} />} label="During break" />
            <FormControlLabel control={<Switch checked={Boolean(form.action_recording?.capture_during_lunch)} onChange={(e) => updateFlat('action_recording.capture_during_lunch', e.target.checked)} />} label="During lunch" />
            <FormControlLabel control={<Switch checked={Boolean(form.action_recording?.capture_after_clock_out)} onChange={(e) => updateFlat('action_recording.capture_after_clock_out', e.target.checked)} />} label="After clock-out" />
            <FormControlLabel control={<Switch checked={Boolean(form.action_recording?.capture_during_emergency)} onChange={(e) => updateFlat('action_recording.capture_during_emergency', e.target.checked)} />} label="During emergency access" />
            <TextField fullWidth margin="dense" type="number" label="Snapshot max width" value={form.action_recording?.snapshot_max_width ?? 1280} onChange={(e) => updateFlat('action_recording.snapshot_max_width', Number(e.target.value))} />
            <TextField fullWidth margin="dense" type="number" label="Snapshot quality" value={form.action_recording?.snapshot_quality ?? 70} onChange={(e) => updateFlat('action_recording.snapshot_quality', Number(e.target.value))} />
          </CardContent></Card>
        </Grid>

        {canReadEmergency && form.emergency_access && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined"><CardContent>
              <Typography variant="h6" gutterBottom>Emergency Access</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Supervisor one-time codes for temporary attendance enforcement bypass. Codes are hashed server-side; no universal escape code.
              </Typography>
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(form.emergency_access.enabled)}
                    disabled={!canConfigureEmergency}
                    onChange={(e) => updateFlat('emergency_access.enabled', e.target.checked)}
                  />
                )}
                label="Emergency Access enabled"
              />
              <TextField
                fullWidth
                margin="dense"
                type="number"
                label="Code redemption expiration (minutes)"
                value={form.emergency_access.code_expiration_minutes ?? 15}
                disabled={!canConfigureEmergency}
                onChange={(e) => updateFlat('emergency_access.code_expiration_minutes', Number(e.target.value))}
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(form.emergency_access.reason_required)}
                    disabled={!canConfigureEmergency}
                    onChange={(e) => updateFlat('emergency_access.reason_required', e.target.checked)}
                  />
                )}
                label="Require reason when generating codes"
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={Boolean(form.alerts?.emergency_bypass)}
                    disabled={!canConfigureEmergency}
                    onChange={(e) => updateFlat('alerts.emergency_bypass', e.target.checked)}
                  />
                )}
                label="Raise EMERGENCY_BYPASS_USED alert when active"
              />
              {!canConfigureEmergency && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  View only — editing requires monitoring.emergency_access.configure.
                </Alert>
              )}
            </CardContent></Card>
          </Grid>
        )}

        {user?.role === 'super_user' && (
          <Grid item xs={12}>
            <BreakGlassMasterPasswordCard />
          </Grid>
        )}

        {canViewDailyEmailConfig && (
          <Grid item xs={12}>
            <Card variant="outlined"><CardContent>
              <Typography variant="h6" gutterBottom>Daily Monitoring Report</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Automated end-of-day workforce monitoring email delivered at the configured local time.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.daily_workforce_report?.enabled)}
                    onChange={(e) => updateFlat('daily_workforce_report.enabled', e.target.checked)}
                    disabled={!canConfigureDailyEmail}
                  />
                }
                label="Enabled"
              />
              <TextField
                fullWidth
                margin="dense"
                label="Delivery time (HH:mm)"
                placeholder="19:00"
                value={form.daily_workforce_report?.delivery_time || '19:00'}
                onChange={(e) => updateFlat('daily_workforce_report.delivery_time', e.target.value)}
                disabled={!canConfigureDailyEmail}
              />
              <TextField
                fullWidth
                margin="dense"
                label="Timezone"
                placeholder="America/Chicago"
                value={form.daily_workforce_report?.timezone || 'America/Chicago'}
                onChange={(e) => updateFlat('daily_workforce_report.timezone', e.target.value)}
                disabled={!canConfigureDailyEmail}
              />
              <TextField
                fullWidth
                margin="dense"
                label="Recipients"
                placeholder="ops@example.com, manager@example.com"
                value={
                  typeof form.daily_workforce_report?.recipients === 'string'
                    ? form.daily_workforce_report.recipients
                    : recipientsToText(form.daily_workforce_report?.recipients)
                }
                onChange={(e) => updateFlat('daily_workforce_report.recipients', e.target.value)}
                disabled={!canConfigureDailyEmail}
                helperText="Comma-separated email addresses"
              />
              <TextField
                fullWidth
                margin="dense"
                label="CC recipients"
                placeholder="support@tekssolutions.com"
                value={
                  typeof form.daily_workforce_report?.cc_recipients === 'string'
                    ? form.daily_workforce_report.cc_recipients
                    : recipientsToText(form.daily_workforce_report?.cc_recipients)
                }
                onChange={(e) => updateFlat('daily_workforce_report.cc_recipients', e.target.value)}
                disabled={!canConfigureDailyEmail}
                helperText="support@tekssolutions.com is NOT included unless listed here"
              />
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Include in email</Typography>
              <FormControlLabel control={<Switch checked={Boolean(form.daily_workforce_report?.include_by_person ?? true)} onChange={(e) => updateFlat('daily_workforce_report.include_by_person', e.target.checked)} disabled={!canConfigureDailyEmail} />} label="By person" />
              <FormControlLabel control={<Switch checked={Boolean(form.daily_workforce_report?.include_by_computer ?? true)} onChange={(e) => updateFlat('daily_workforce_report.include_by_computer', e.target.checked)} disabled={!canConfigureDailyEmail} />} label="By computer" />
              <FormControlLabel control={<Switch checked={Boolean(form.daily_workforce_report?.include_apps ?? true)} onChange={(e) => updateFlat('daily_workforce_report.include_apps', e.target.checked)} disabled={!canConfigureDailyEmail} />} label="Apps" />
              <FormControlLabel control={<Switch checked={Boolean(form.daily_workforce_report?.include_domains ?? true)} onChange={(e) => updateFlat('daily_workforce_report.include_domains', e.target.checked)} disabled={!canConfigureDailyEmail} />} label="Domains" />
              <FormControlLabel control={<Switch checked={Boolean(form.daily_workforce_report?.include_evidence ?? true)} onChange={(e) => updateFlat('daily_workforce_report.include_evidence', e.target.checked)} disabled={!canConfigureDailyEmail} />} label="Evidence" />
              <FormControlLabel control={<Switch checked={Boolean(form.daily_workforce_report?.include_attention ?? true)} onChange={(e) => updateFlat('daily_workforce_report.include_attention', e.target.checked)} disabled={!canConfigureDailyEmail} />} label="Attention" />
              <FormControlLabel control={<Switch checked={Boolean(form.daily_workforce_report?.include_narrative ?? true)} onChange={(e) => updateFlat('daily_workforce_report.include_narrative', e.target.checked)} disabled={!canConfigureDailyEmail} />} label="Narrative" />
              <FormControlLabel control={<Switch checked={Boolean(form.daily_workforce_report?.include_unlinked ?? true)} onChange={(e) => updateFlat('daily_workforce_report.include_unlinked', e.target.checked)} disabled={!canConfigureDailyEmail} />} label="Unlinked" />
              <FormControlLabel control={<Switch checked={Boolean(form.daily_workforce_report?.include_missing ?? true)} onChange={(e) => updateFlat('daily_workforce_report.include_missing', e.target.checked)} disabled={!canConfigureDailyEmail} />} label="Missing" />
              {!canConfigureDailyEmail && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  View only — saving daily email settings requires monitoring.activity_reports.configure.
                </Alert>
              )}
            </CardContent></Card>
          </Grid>
        )}

        {canReadProductivity && (
          <Grid item xs={12}>
            <Card variant="outlined"><CardContent>
              <Typography variant="h6" gutterBottom>Productivity</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Factual metrics are on by default. The composite Productivity Score stays OFF until Super Admin enables it after pilot validation.
                Action/click counts never affect the score.
              </Typography>
              <Tabs value={prodTab} onChange={(_, v) => setProdTab(v)} sx={{ mb: 2 }}>
                <Tab label="Metrics" />
                <Tab label="Classifications" />
                <Tab label="Score" />
                <Tab label="Thresholds" />
              </Tabs>

              {prodTab === 0 && (
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(form.productivity?.metrics_enabled ?? true)}
                      disabled={!canConfigureProductivity}
                      onChange={(e) => updateFlat('productivity.metrics_enabled', e.target.checked)}
                    />
                  )}
                  label="Productivity metrics enabled (Active Work Ratio, idle/locked ratios)"
                />
              )}

              {prodTab === 1 && (
                <Box>
                  {!canReadClassifications ? (
                    <Alert severity="info">Classification management requires monitoring.productivity.classifications.read.</Alert>
                  ) : (
                    <>
                      {canManageClassifications && (
                        <Box display="flex" gap={1} flexWrap="wrap" mb={2} alignItems="center">
                          <TextField
                            select
                            size="small"
                            label="Type"
                            value={classForm.target_type}
                            onChange={(e) => setClassForm((p) => ({ ...p, target_type: e.target.value }))}
                            sx={{ minWidth: 140 }}
                          >
                            <MenuItem value="APPLICATION">Application</MenuItem>
                            <MenuItem value="DOMAIN">Domain</MenuItem>
                          </TextField>
                          <TextField
                            size="small"
                            label={classForm.target_type === 'DOMAIN' ? 'Domain' : 'Application'}
                            value={classForm.target_key}
                            onChange={(e) => setClassForm((p) => ({ ...p, target_key: e.target.value }))}
                          />
                          <TextField
                            select
                            size="small"
                            label="Classification"
                            value={classForm.classification}
                            onChange={(e) => setClassForm((p) => ({ ...p, classification: e.target.value }))}
                            sx={{ minWidth: 160 }}
                          >
                            {['PRODUCTIVE', 'NEUTRAL', 'NON_PRODUCTIVE', 'BLOCKED', 'UNCLASSIFIED'].map((c) => (
                              <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            size="small"
                            label="Campaign override ID (optional)"
                            value={classForm.campaign_id}
                            onChange={(e) => setClassForm((p) => ({ ...p, campaign_id: e.target.value }))}
                            sx={{ minWidth: 220 }}
                          />
                          <Button
                            variant="contained"
                            disabled={!classForm.target_key || saveClassificationMutation.isPending}
                            onClick={() => saveClassificationMutation.mutate({
                              target_type: classForm.target_type,
                              target_key: classForm.target_key,
                              classification: classForm.classification,
                              campaign_id: classForm.campaign_id || null,
                            })}
                          >
                            Save Rule
                          </Button>
                        </Box>
                      )}
                      <Typography variant="subtitle2" gutterBottom>Rules</Typography>
                      {(classificationsData?.data?.data || []).slice(0, 40).map((rule: any) => (
                        <Box key={rule.id} display="flex" gap={1} alignItems="center" mb={0.5}>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {rule.targetType || rule.target_type}: {rule.targetKey || rule.target_key}
                            {' → '}{rule.classification}
                            {(rule.campaignId || rule.campaign_id) ? ` (campaign ${rule.campaignId || rule.campaign_id})` : ' (global)'}
                          </Typography>
                          {canManageClassifications && (
                            <Button size="small" color="error" onClick={() => deleteClassificationMutation.mutate(rule.id)}>Delete</Button>
                          )}
                        </Box>
                      ))}
                      <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Unclassified applications</Typography>
                      {(unclassifiedData?.data?.data?.applications || []).slice(0, 15).map((item: any) => (
                        <Box key={item.key} display="flex" gap={1} alignItems="center" mb={0.5}>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {item.key} · {item.employee_count} employees · {Math.round((item.active_seconds || 0) / 60)}m
                          </Typography>
                          {canManageClassifications && ['PRODUCTIVE', 'NEUTRAL', 'NON_PRODUCTIVE'].map((c) => (
                            <Button
                              key={c}
                              size="small"
                              onClick={() => saveClassificationMutation.mutate({
                                target_type: 'APPLICATION',
                                target_key: item.key,
                                classification: c,
                              })}
                            >
                              Mark {c.replace('_', ' ').toLowerCase()}
                            </Button>
                          ))}
                        </Box>
                      ))}
                      <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>Unclassified domains</Typography>
                      {(unclassifiedData?.data?.data?.domains || []).slice(0, 15).map((item: any) => (
                        <Box key={item.key} display="flex" gap={1} alignItems="center" mb={0.5}>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {item.key} · {item.employee_count} employees · {Math.round((item.active_seconds || 0) / 60)}m
                          </Typography>
                          {canManageClassifications && ['PRODUCTIVE', 'NEUTRAL', 'NON_PRODUCTIVE'].map((c) => (
                            <Button
                              key={c}
                              size="small"
                              onClick={() => saveClassificationMutation.mutate({
                                target_type: 'DOMAIN',
                                target_key: item.key,
                                classification: c,
                              })}
                            >
                              Mark {c.replace('_', ' ').toLowerCase()}
                            </Button>
                          ))}
                        </Box>
                      ))}
                    </>
                  )}
                </Box>
              )}

              {prodTab === 2 && (
                <Box>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(form.productivity?.score_enabled ?? false)}
                        disabled={!canConfigureProductivity}
                        onChange={(e) => updateFlat('productivity.score_enabled', e.target.checked)}
                      />
                    )}
                    label="Productivity Score enabled (OFF by default)"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Weights must total 100. Formula version: {form.productivity?.formula_version || 'TEKS_PRODUCTIVITY_V1'}
                  </Typography>
                  <Grid container spacing={1}>
                    {(['attendance', 'active_work', 'productive_apps', 'schedule_compliance'] as const).map((key) => (
                      <Grid item xs={6} md={3} key={key}>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          label={key}
                          disabled={!canConfigureProductivity}
                          value={form.productivity?.weights?.[key] ?? ''}
                          onChange={(e) => updateFlat(`productivity.weights.${key}`, Number(e.target.value))}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {prodTab === 3 && (
                <Grid container spacing={1}>
                  {[
                    ['min_eligible_work_seconds', 'Min eligible work (sec)'],
                    ['min_agent_coverage_percent', 'Min agent coverage %'],
                    ['min_classification_coverage_percent', 'Min classification coverage %'],
                    ['idle_review_seconds', 'Idle review threshold (sec)'],
                    ['today_so_far_min_eligible_seconds', 'Today-so-far min eligible (sec)'],
                  ].map(([key, label]) => (
                    <Grid item xs={12} md={6} key={key}>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        label={label}
                        disabled={!canConfigureProductivity}
                        value={form.productivity?.thresholds?.[key] ?? ''}
                        onChange={(e) => updateFlat(`productivity.thresholds.${key}`, Number(e.target.value))}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}

              {!canConfigureProductivity && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  View only — saving productivity settings requires monitoring.productivity.configure.
                </Alert>
              )}
            </CardContent></Card>
          </Grid>
        )}

        {canConfigureLiveScreen && (
          <Grid item xs={12}>
            <Card variant="outlined"><CardContent>
              <Typography variant="h6" gutterBottom>Live Screen</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Master switch for on-demand Live Screen. Capture is device-centric — it does not depend on Clock In, Lunch, Break, schedule, or TEKS login. Streaming only runs while an authorized viewer is subscribed.
              </Typography>
              {liveScreenLoading || !liveForm ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <FormControlLabel
                    control={<Switch checked={Boolean(liveForm.enabled)} onChange={(e) => patchLive({ enabled: e.target.checked })} />}
                    label="Enabled"
                  />
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Thumbnail</Typography>
                      <TextField size="small" fullWidth margin="dense" type="number" label="Width" value={liveForm.thumbnail?.width ?? ''} onChange={(e) => patchProfile('thumbnail', 'width', Number(e.target.value))} />
                      <TextField size="small" fullWidth margin="dense" type="number" label="Height" value={liveForm.thumbnail?.height ?? ''} onChange={(e) => patchProfile('thumbnail', 'height', Number(e.target.value))} />
                      <TextField size="small" fullWidth margin="dense" type="number" label="Max FPS" inputProps={{ step: 0.1 }} value={liveForm.thumbnail?.maxFps ?? ''} onChange={(e) => patchProfile('thumbnail', 'maxFps', Number(e.target.value))} />
                      <TextField size="small" fullWidth margin="dense" type="number" label="Quality" value={liveForm.thumbnail?.quality ?? ''} onChange={(e) => patchProfile('thumbnail', 'quality', Number(e.target.value))} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>Detail</Typography>
                      <TextField size="small" fullWidth margin="dense" type="number" label="Width" value={liveForm.detail?.width ?? ''} onChange={(e) => patchProfile('detail', 'width', Number(e.target.value))} />
                      <TextField size="small" fullWidth margin="dense" type="number" label="Height" value={liveForm.detail?.height ?? ''} onChange={(e) => patchProfile('detail', 'height', Number(e.target.value))} />
                      <TextField size="small" fullWidth margin="dense" type="number" label="Max FPS" value={liveForm.detail?.maxFps ?? ''} onChange={(e) => patchProfile('detail', 'maxFps', Number(e.target.value))} />
                      <TextField size="small" fullWidth margin="dense" type="number" label="Quality" value={liveForm.detail?.quality ?? ''} onChange={(e) => patchProfile('detail', 'quality', Number(e.target.value))} />
                    </Grid>
                  </Grid>
                  <Box display="flex" gap={1.5} flexWrap="wrap" mt={1}>
                    <TextField size="small" type="number" label="Max active thumbnails" value={liveForm.maxActiveThumbnails ?? ''} onChange={(e) => patchLive({ maxActiveThumbnails: Number(e.target.value) })} />
                    <TextField size="small" type="number" label="Stale frame threshold (sec)" value={liveForm.staleFrameThresholdSeconds ?? ''} onChange={(e) => patchLive({ staleFrameThresholdSeconds: Number(e.target.value) })} />
                  </Box>
                  <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button variant="contained" onClick={handleSaveLive} disabled={saveLiveMutation.isPending}>
                      Save Live Screen
                    </Button>
                    {liveSaved && <Alert severity="success">Live Screen settings saved.</Alert>}
                  </Box>
                </>
              )}
            </CardContent></Card>
          </Grid>
        )}

        {canReadRemoteControl && (
          <Grid item xs={12}>
            <Card variant="outlined"><CardContent>
              <Typography variant="h6" gutterBottom>Remote Control</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Master switch for authorized remote mouse/keyboard control. Default is OFF. Live Screen viewing remains independent — disabling Remote Control does not affect viewing.
              </Typography>
              {remoteControlLoading || !remoteForm ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(remoteForm.enabled)}
                        disabled={!canEditRemoteControl}
                        onChange={(e) => patchRemote({ enabled: e.target.checked })}
                      />
                    )}
                    label="Remote Control Enabled"
                  />
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(remoteForm.showIndicatorToEmployee)}
                        disabled={!canEditRemoteControl}
                        onChange={(e) => patchRemote({ showIndicatorToEmployee: e.target.checked })}
                      />
                    )}
                    label="Show indicator to employee"
                  />
                  <Box sx={{ mt: 1, maxWidth: 320 }}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Inactivity timeout (minutes)"
                      disabled={!canEditRemoteControl}
                      value={remoteForm.inactivityTimeoutMinutes ?? 15}
                      onChange={(e) => patchRemote({ inactivityTimeoutMinutes: Number(e.target.value) })}
                      inputProps={{ min: 1, max: 240 }}
                    />
                  </Box>
                  <Box sx={{ mt: 1.5, maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Clipboard max bytes"
                      disabled={!canEditRemoteControl}
                      value={remoteForm.clipboardMaxBytes ?? 1048576}
                      onChange={(e) => patchRemote({ clipboardMaxBytes: Number(e.target.value) })}
                      inputProps={{ min: 1 }}
                      helperText="Text-only clipboard transfer size limit (UTF-8 bytes)."
                    />
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={remoteForm.chatEnabled !== false}
                          disabled={!canEditRemoteControl}
                          onChange={(e) => patchRemote({ chatEnabled: e.target.checked })}
                        />
                      )}
                      label="Chat enabled"
                    />
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Chat max message chars"
                      disabled={!canEditRemoteControl || remoteForm.chatEnabled === false}
                      value={remoteForm.chatMaxMessageChars ?? 2000}
                      onChange={(e) => patchRemote({ chatMaxMessageChars: Number(e.target.value) })}
                      inputProps={{ min: 1, max: 10000 }}
                    />
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={remoteForm.filesEnabled !== false}
                          disabled={!canEditRemoteControl}
                          onChange={(e) => patchRemote({ filesEnabled: e.target.checked })}
                        />
                      )}
                      label="Files enabled"
                    />
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="File max bytes"
                      disabled={!canEditRemoteControl || remoteForm.filesEnabled === false}
                      value={remoteForm.fileMaxBytes ?? 52428800}
                      onChange={(e) => patchRemote({ fileMaxBytes: Number(e.target.value) })}
                      inputProps={{ min: 1 }}
                      helperText="Max size per remote support file transfer (default 50 MB)."
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="Support folder name"
                      disabled={!canEditRemoteControl || remoteForm.filesEnabled === false}
                      value={remoteForm.supportFolderName ?? 'TEKS Support'}
                      onChange={(e) => patchRemote({ supportFolderName: e.target.value })}
                      helperText="Folder name under the employee profile used for transfers."
                    />
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={remoteForm.actionsEnabled !== false}
                          disabled={!canEditRemoteControl}
                          onChange={(e) => patchRemote({ actionsEnabled: e.target.checked })}
                        />
                      )}
                      label="Actions enabled"
                    />
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Retention (days)</Typography>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Session retention days"
                      disabled={!canEditRemoteControl}
                      value={remoteForm.sessionRetentionDays ?? 90}
                      onChange={(e) => patchRemote({ sessionRetentionDays: Number(e.target.value) })}
                      inputProps={{ min: 1, max: 3650 }}
                      helperText="Ended remote support sessions metadata retention."
                    />
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Chat retention days"
                      disabled={!canEditRemoteControl}
                      value={remoteForm.chatRetentionDays ?? 90}
                      onChange={(e) => patchRemote({ chatRetentionDays: Number(e.target.value) })}
                      inputProps={{ min: 1, max: 3650 }}
                      helperText="Support chat message retention."
                    />
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      label="Transfer retention days"
                      disabled={!canEditRemoteControl}
                      value={remoteForm.transferRetentionDays ?? 30}
                      onChange={(e) => patchRemote({ transferRetentionDays: Number(e.target.value) })}
                      inputProps={{ min: 1, max: 3650 }}
                      helperText="Remote file transfer files and rows retention."
                    />
                  </Box>
                  {!canEditRemoteControl && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      View only — saving Remote Control settings requires monitoring.remote_control.start.
                    </Alert>
                  )}
                  {canEditRemoteControl && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        onClick={handleSaveRemote}
                        disabled={saveRemoteMutation.isPending}
                      >
                        Save Remote Control
                      </Button>
                      {remoteSaved && <Alert severity="success">Remote Control settings saved.</Alert>}
                    </Box>
                  )}
                </>
              )}
            </CardContent></Card>
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button variant="contained" onClick={handleSave} disabled={saveMutation.isPending}>Save Configuration</Button>
        {saved && <Alert severity="success">Configuration saved.</Alert>}
        {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
      </Box>

      <MonitoringDisclosureCard />
    </Box>
  );
};

export default ConfigurationPage;
